import {
  Card,
  CardBody,
  Flex,
  Grid,
  GridItem,
  Stack,
  Stat,
  StatGroup,
  StatLabel,
  StatNumber,
  Tab,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { HypotheticalTabProps } from "features/financial-reporting/tabs/HypotheticalTabProps";
import {
  useHypotheticalWaterfallFundPerformanceDetailsRetrieve,
  useHypotheticalWaterfallPerformanceBreakdownRetrieve,
  useHypotheticalWaterfallPerformanceMultiplesRetrieve,
} from "gen/endpoints/financial-reporting/financial-reporting";
import { useFundListSuspense } from "gen/endpoints/fund-entities/fund-entities";
import { GenericTableData } from "gen/model/genericTableData";
import { HypotheticalFundDetailsData } from "gen/model/hypotheticalFundDetailsData";
import { HypotheticalWaterfallPerformanceBreakdownAPIData } from "gen/model/hypotheticalWaterfallPerformanceBreakdownAPIData";
import { HypotheticalWaterfallPerformanceMultiplesAPIData } from "gen/model/hypotheticalWaterfallPerformanceMultiplesAPIData";
import { toLower } from "lodash-es";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { LoadingState } from "shared/components/empty/LoadingState";
import { FundDropdown } from "shared/components/input/dropdowns/FundDropdown";
import { PageTabBody } from "shared/components/layout/PageTabBody";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTabs } from "shared/components/table/box/TableBoxTabs";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { GetLinkInfoForCellFn } from "shared/components/table/data/DataTableTypes";
import { useFeatureFlagActive } from "shared/hooks/useFeatureFlag";
import { rootRoute } from "shared/navigation/Routes";
import { formatPercentage } from "shared/utils/formattingUtils";
import { formatMoney } from "shared/utils/MoneyUtils";
import { NonNullStringParam } from "shared/utils/QueryParamUtils";
import { useQueryParam } from "use-query-params";

export const HypotheticalTabPerformance: React.FC = () => {
  const { fundFamilyId, hypotheticalWaterfallId, endOfPeriodReportId } =
    useOutletContext<HypotheticalTabProps>();
  const { t } = useTranslation(["financialReporting", "common"]);
  const [fundId, setFundId] = useQueryParam("fundId", NonNullStringParam);
  const { data: funds } = useFundListSuspense({
    fund_family_ids: [fundFamilyId],
    require_commitment: true,
  });
  const { data: hypotheticalBreakdownData, isLoading } =
    useHypotheticalWaterfallPerformanceBreakdownRetrieve(
      fundFamilyId,
      endOfPeriodReportId,
      hypotheticalWaterfallId,
      {
        fund_id: fundId,
      },
    );

  const { data: hypotheticalMultiplesData } =
    useHypotheticalWaterfallPerformanceMultiplesRetrieve(
      fundFamilyId,
      endOfPeriodReportId,
      hypotheticalWaterfallId,
      {
        fund_id: fundId,
      },
    );

  const { data: hypotheticalFundPerformanceDetailsData } =
    useHypotheticalWaterfallFundPerformanceDetailsRetrieve(
      fundFamilyId,
      endOfPeriodReportId,
      hypotheticalWaterfallId,
      {
        fund_id: fundId,
      },
    );

  const getLinkInfoForCell = useCallback<GetLinkInfoForCellFn>(
    (props) => {
      if (
        props.columnContext.column.id == "primary" &&
        props.type == "data" &&
        props.row.data?.metadata?.type == "irr_row"
      ) {
        const irrTableRoute = rootRoute({})
          .fundFamilies({})
          .fundFamily({ fundFamilyId })
          .financialReporting({})
          .hypotheticalWaterfall({
            hypotheticalWaterfallId,
            endOfPeriodReportId,
          })
          .irr({}).$;
        const finalRoute = `${irrTableRoute}?${props.row.data.metadata.granularity_level}=${props.row.data.metadata.granularity_level_id}`;
        return {
          type: "generic",
          to: finalRoute,
        };
      }
      return undefined;
    },
    [fundFamilyId, endOfPeriodReportId, hypotheticalWaterfallId],
  );

  const [tabIndex, setTabIndex] = useState(0);
  const indexToData: Record<number, GenericTableData> =
    isLoading || !hypotheticalBreakdownData
      ? {}
      : {
          0: hypotheticalBreakdownData.fund_table_data,
          1: hypotheticalBreakdownData.investor_class_table_data,
          2: hypotheticalBreakdownData.investor_table_data,
          3: hypotheticalBreakdownData.security_ownership_table_data,
        };

  return (
    <PageTabBody flex={1} isFullHeight={false} pt="8px">
      {isLoading && (
        <LoadingState
          height="100%"
          width="100%"
          message={t("financialReporting:loading", {
            item: toLower(t("financialReporting:performance")),
          })}
          display="flex"
        />
      )}
      <Flex justifyContent="flex-end" pb="8px">
        <FundDropdown
          fundId={fundId}
          onChangeFundId={setFundId}
          funds={funds}
        />
      </Flex>
      <PerformanceSummary
        breakdownData={hypotheticalBreakdownData}
        multiplesData={hypotheticalMultiplesData}
        fundPerformanceDetailsData={hypotheticalFundPerformanceDetailsData}
      />
      {!isLoading && (
        <TableBox isFullWidth={true} key={tabIndex}>
          <TableBoxHeader>
            <TableBoxTabs index={tabIndex} onChange={setTabIndex}>
              <Tab>{t("common:fund")}</Tab>
              <Tab>{t("common:investorClass")}</Tab>
              <Tab>{t("common:investor")}</Tab>
              <Tab>{t("common:commitments")}</Tab>
            </TableBoxTabs>
            <DataTableBoxActions />
          </TableBoxHeader>
          <TableBoxBody>
            <DataTable
              data={indexToData[tabIndex]}
              getLinkInfoForCell={getLinkInfoForCell}
            />
          </TableBoxBody>
        </TableBox>
      )}
    </PageTabBody>
  );
};

const PerformanceSummary: React.FC<{
  breakdownData: HypotheticalWaterfallPerformanceBreakdownAPIData | undefined;
  multiplesData: HypotheticalWaterfallPerformanceMultiplesAPIData | undefined;
  fundPerformanceDetailsData: HypotheticalFundDetailsData | undefined;
}> = ({ breakdownData, multiplesData, fundPerformanceDetailsData }) => {
  const { t } = useTranslation(["financialReporting", "common"]);
  const isTimeWeightedReturnDisplayActive = useFeatureFlagActive(
    "display_time_weighted_returns",
  );
  if (!breakdownData || !multiplesData || !fundPerformanceDetailsData) {
    return null;
  }
  return (
    <Stack
      direction={{ base: "column", md: "column", xl: "row" }}
      marginBottom="16px"
      spacing="24px"
    >
      <Card py="0px" flex={{ xl: 2 }} minWidth="500px">
        <TableContainer>
          <Table>
            <Thead>
              <Tr>
                <Th>{t("financialReporting:table.metric")}</Th>
                <Th textAlign="end">{t("financialReporting:table.amount")}</Th>
              </Tr>
            </Thead>
            <Tbody>
              <Tr key="fund_size">
                <Th>{t("financialReporting:metric_names.fund_size")}</Th>
                <Td textAlign="end">
                  {formatMoney(fundPerformanceDetailsData.fund_size)}
                </Td>
              </Tr>
              <Tr key="fund_nav">
                <Th>{t("financialReporting:metric_names.fund_nav")}</Th>
                <Td textAlign="end">
                  {formatMoney(fundPerformanceDetailsData.fund_nav)}
                </Td>
              </Tr>
              <Tr key="total_contributions">
                <Th>
                  {t("financialReporting:metric_names.total_contributions")}
                </Th>
                <Td textAlign="end">
                  {formatMoney(fundPerformanceDetailsData.total_capital_called)}
                </Td>
              </Tr>
              <Tr key="total_distributions">
                <Th>
                  {t("financialReporting:metric_names.total_distributions")}
                </Th>
                <Td textAlign="end">
                  {formatMoney(fundPerformanceDetailsData.total_distributions)}
                </Td>
              </Tr>
              <Tr key="total_recallable">
                <Th>{t("financialReporting:metric_names.total_recallable")}</Th>
                <Td textAlign="end">
                  {formatMoney(
                    fundPerformanceDetailsData.total_recallable_distributions,
                  )}
                </Td>
              </Tr>
              <Tr key="total_fund_level_debt">
                <Th>
                  {t("financialReporting:metric_names.total_fund_level_debt")}
                </Th>
                <Td textAlign="end">
                  {formatMoney(
                    fundPerformanceDetailsData.total_debt_outstanding,
                  )}
                </Td>
              </Tr>
            </Tbody>
          </Table>
        </TableContainer>
      </Card>
      <Card variant="stat" minWidth="230px" flex={{ xl: 1 }}>
        <CardBody>
          <StatGroup
            py="24px !important"
            gap="24px !important"
            alignItems="stretch"
            flexDirection={{ base: "row", md: "row", xl: "column" }}
          >
            {Object.entries(
              multiplesData.investment_multiple_name_to_evaluated_amount,
            ).map(([performance_multiple_name, multiple_value], index) => (
              <Stat size="sm" key={index}>
                <StatLabel> {performance_multiple_name}</StatLabel>
                <StatNumber>{`${multiple_value}x`}</StatNumber>
              </Stat>
            ))}
          </StatGroup>
        </CardBody>
      </Card>
      <Card
        p={{ base: "30px", md: "30px", xl: "64px" }}
        flex={{ xl: 2 }}
        minWidth="500px"
      >
        <CardBody display="flex" justifyContent="center" alignItems="center">
          <Grid
            templateRows={{
              base: "repeat(1, 1fr)",
              md: "repeat(1, 1fr)",
              xl: "repeat(1, 1fr)",
            }}
            templateColumns={{
              base: "repeat(4, 1fr)",
              md: "repeat(4, 1fr)",
              lg: "repeat(2, 1fr)",
            }}
            gap={6}
          >
            <GridItem key="grossIrr" rowSpan={1} colSpan={1}>
              <Stat size="md">
                <StatNumber>
                  {formatPercentage(
                    breakdownData.fund_family_metrics_for_hypo.total_gross_irr,
                  )}
                </StatNumber>
                <StatLabel maxWidth="200px" whiteSpace="normal">
                  {t("financialReporting:performanceMetrics.grossIRR")}
                </StatLabel>
              </Stat>
            </GridItem>
            <GridItem key="netIrr" rowSpan={1} colSpan={1}>
              <Stat size="md">
                <StatNumber>
                  {formatPercentage(
                    breakdownData.fund_family_metrics_for_hypo.total_net_irr,
                  )}
                </StatNumber>
                <StatLabel maxWidth="200px" whiteSpace="normal">
                  {t("financialReporting:performanceMetrics.netIRR")}
                </StatLabel>
              </Stat>
            </GridItem>
            {isTimeWeightedReturnDisplayActive && (
              <GridItem key="grossTimeWeightedReturns" rowSpan={1} colSpan={1}>
                <Stat size="md">
                  <StatNumber>
                    {breakdownData.fund_family_metrics_for_hypo
                      .gross_time_weighted_return
                      ? formatPercentage(
                          breakdownData.fund_family_metrics_for_hypo
                            .gross_time_weighted_return,
                        )
                      : "N/A"}
                  </StatNumber>
                  <StatLabel maxWidth="200px" whiteSpace="normal">
                    {t("financialReporting:performanceMetrics.grossTWR")}
                  </StatLabel>
                </Stat>
              </GridItem>
            )}
            {isTimeWeightedReturnDisplayActive && (
              <GridItem key="netTimeWeightedReturns" rowSpan={1} colSpan={1}>
                <Stat size="md">
                  <StatNumber>
                    {breakdownData.fund_family_metrics_for_hypo
                      .net_time_weighted_return
                      ? formatPercentage(
                          breakdownData.fund_family_metrics_for_hypo
                            .net_time_weighted_return,
                        )
                      : "N/A"}
                  </StatNumber>
                  <StatLabel maxWidth="200px" whiteSpace="normal">
                    {t("financialReporting:performanceMetrics.netTWR")}
                  </StatLabel>
                </Stat>
              </GridItem>
            )}
          </Grid>
        </CardBody>
      </Card>
    </Stack>
  );
};
